import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    InputSignal,
    InputSignalWithTransform,
    OnDestroy,
    OutputEmitterRef,
    WritableSignal,
    afterNextRender,
    booleanAttribute,
    effect,
    inject,
    input,
    output,
    signal,
} from '@angular/core';

import { BlockDirective, ElemDirective, ModDirective } from '@rt-tools/core';
import { RtButtonDirective } from '@rt-tools/ui-kit-v2';
import { IPlanPoint, stairPlacement } from '@stair-craft/calc/util';
import { IStairCalculationResult, IStairConfig } from '@stair-craft/common/util';
import { Group, Material, MeshStandardMaterial, Object3D, WebGLProgramParametersWithUniforms } from 'three';

import { IOpeningDragEvent, OpeningDragService } from './opening-drag.service';
import { RoomMeshBuilder } from './room-mesh-builder.service';
import { readSceneGridColors } from './scene-background';
import { SceneService, isMesh } from './scene.service';
import { StairMeshBuilder } from './stair-mesh-builder.service';

const PAN_STEP_MM: number = 300;
const PAN_HOLD_STEP_MM: number = 50;
const ORBIT_STEP_RAD: number = Math.PI / 18;
const ORBIT_HOLD_STEP_RAD: number = Math.PI / 180;
const ZOOM_STEP_MM: number = 500;
const ZOOM_HOLD_STEP_MM: number = 100;
const HOLD_DELAY_MS: number = 250;
const HOLD_TICK_MS: number = 50;
const BEM_BLOCK: string = 'sc-viewport';

@Component({
    selector: 'sc-viewport',
    templateUrl: './viewport.component.html',
    styleUrl: './viewport.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        // rt-tools
        BlockDirective,
        ElemDirective,
        ModDirective,
        RtButtonDirective,
    ],
    providers: [SceneService, StairMeshBuilder, RoomMeshBuilder, OpeningDragService],
    host: {
        class: BEM_BLOCK,
    },
})
export class ViewportComponent implements OnDestroy {
    readonly #scene: SceneService = inject(SceneService);
    readonly #meshBuilder: StairMeshBuilder = inject(StairMeshBuilder);
    readonly #roomBuilder: RoomMeshBuilder = inject(RoomMeshBuilder);
    readonly #openingDrag: OpeningDragService = inject(OpeningDragService);
    readonly #hostEl: ElementRef = inject(ElementRef);

    #resizeObserver: ResizeObserver | null = null;
    #dragInitialized: boolean = false;
    #holdTimeout: ReturnType<typeof globalThis.setTimeout> | null = null;
    #holdInterval: ReturnType<typeof globalThis.setInterval> | null = null;
    // Grayscale clones are made fresh per rebuild; they are not in the builder's cache,
    // so they must be disposed here or they leak one material set per rebuild.
    #monochromeClones: MeshStandardMaterial[] = [];

    protected readonly controlsVisible: WritableSignal<boolean> = signal<boolean>(true);

    public readonly result: InputSignal<IStairCalculationResult | null> = input<IStairCalculationResult | null>(null);
    public readonly config: InputSignal<IStairConfig | null> = input<IStairConfig | null>(null);
    public readonly hideControls: InputSignalWithTransform<boolean, unknown> = input<boolean, unknown>(false, {
        transform: booleanAttribute,
    });
    public readonly monochrome: InputSignalWithTransform<boolean, unknown> = input<boolean, unknown>(false, {
        transform: booleanAttribute,
    });
    public readonly openingDragChange: OutputEmitterRef<IOpeningDragEvent> = output<IOpeningDragEvent>();

    constructor() {
        afterNextRender(() => {
            // A finger already orbits, pinches and pans the scene; on a touch screen the button
            // groups only cover the stair, so they start folded behind the toggle.
            if (globalThis.matchMedia('(pointer: coarse)').matches) {
                this.controlsVisible.set(false);
            }
            const el: HTMLElement = this.#hostEl.nativeElement as HTMLElement;
            const canvas: HTMLCanvasElement = el.querySelector('canvas') ?? document.createElement('canvas');
            this.#scene.init(canvas, el.clientWidth, el.clientHeight);
            // The grid colours are page tokens, read from the canvas's document like the background.
            this.#roomBuilder.setGridColors(readSceneGridColors(canvas));

            this.#resizeObserver = new ResizeObserver(() => {
                this.#scene.resize(el.clientWidth, el.clientHeight);
            });
            this.#resizeObserver.observe(el);

            this.#initDragIfNeeded(canvas);
            this.#rebuildMeshes();
        });

        effect(() => {
            const result: IStairCalculationResult | null = this.result();
            const config: IStairConfig | null = this.config();
            this.monochrome();
            if (this.#scene.renderer) {
                this.#rebuildMeshes(result, config);
            }
        });
    }

    #initDragIfNeeded(canvas?: HTMLCanvasElement): void {
        if (this.#dragInitialized) {
            return;
        }
        const result: IStairCalculationResult | null = this.result();
        const el: HTMLElement = this.#hostEl.nativeElement as HTMLElement;
        const targetCanvas: HTMLCanvasElement | null = canvas ?? el.querySelector('canvas');
        if (!result || !targetCanvas) {
            return;
        }

        this.#openingDrag.init(this.#scene, targetCanvas, result.room, (event: IOpeningDragEvent) => {
            this.openingDragChange.emit(event);
        });
        this.#dragInitialized = true;
    }

    #rebuildMeshes(result: IStairCalculationResult | null = this.result(), config: IStairConfig | null = this.config()): void {
        this.#scene.clearStair();
        this.#scene.clearRoom();
        this.#disposeMonochromeClones();

        if (!result || !config) {
            return;
        }

        this.#initDragIfNeeded();

        const roomGroup: Group = this.#roomBuilder.buildAll(result.room);
        this.#scene.addToRoom(roomGroup);
        this.#openingDrag.updateRoom(result.room);

        const { stairGroup, upperFloorGroup }: { stairGroup: Group; upperFloorGroup: Group } = this.#meshBuilder.buildAll(
            result.geometry,
            config.treadMaterialId,
            config.stringerMaterialId,
            config.riserMaterialId,
            config.stringerType,
            new Set<number>(result.warningStepIndices),
            { handrailMaterialId: config.handrailMaterialId, balusterMaterialId: config.balusterMaterialId }
        );

        if (this.monochrome()) {
            this.#applyMonochrome(stairGroup);
            this.#applyMonochrome(upperFloorGroup);
            this.#applyMonochrome(roomGroup);
        }

        this.#scene.addToStair(stairGroup);
        // Upper-floor railing is in WORLD coords — render in the room group so it stays at the
        // opening edge regardless of how the stair group is positioned.
        this.#scene.addToRoom(upperFloorGroup);

        // The stair stands where the calculators measured its headroom: one placement for both.
        const placement: IPlanPoint = stairPlacement(config, result.room, result.totalRun);
        this.#scene.setStairPosition(placement.x, 0, placement.z);

        this.#openingDrag.updateConstraints({
            stairWidth: config.stairWidth,
            totalRun: result.totalRun,
        });
        this.#openingDrag.setLocked(config.openingLocked);
        this.#scene.requestRender();
    }

    #applyMonochrome(group: Group): void {
        group.traverse((obj: Object3D): void => {
            if (!isMesh(obj)) {
                return;
            }
            const source: Material | Material[] = obj.material;
            if (Array.isArray(source)) {
                obj.material = source.map((mat: Material) => this.#toGrayscaleMaterial(mat));
            } else {
                obj.material = this.#toGrayscaleMaterial(source);
            }
        });
    }

    #toGrayscaleMaterial(mat: Material): Material {
        if (!(mat instanceof MeshStandardMaterial)) {
            return mat;
        }
        const clone: MeshStandardMaterial = mat.clone();
        this.#monochromeClones.push(clone);
        // Desaturate diffuse (base color × texture) after the map is sampled and before
        // lighting. Keeps texture detail and per-material distinction; PBR shading still works.
        clone.onBeforeCompile = (shader: WebGLProgramParametersWithUniforms): void => {
            shader.fragmentShader = shader.fragmentShader.replace(
                '#include <map_fragment>',
                `
                #include <map_fragment>
                float scMonoLum = dot(diffuseColor.rgb, vec3(0.2126, 0.7152, 0.0722));
                diffuseColor.rgb = vec3(scMonoLum);
                `
            );
        };
        clone.needsUpdate = true;
        return clone;
    }

    #disposeMonochromeClones(): void {
        for (const clone of this.#monochromeClones) {
            clone.dispose();
        }
        this.#monochromeClones = [];
    }

    public ngOnDestroy(): void {
        this.#resizeObserver?.disconnect();
        this.#disposeMonochromeClones();
        this.#meshBuilder.dispose();
        this.#roomBuilder.dispose();
        this.#clearHold();
    }

    protected startPan(dx: number, dy: number, event: PointerEvent): void {
        this.#capturePointer(event);
        this.#scene.panCamera(dx * PAN_STEP_MM, dy * PAN_STEP_MM);
        this.#scheduleHold(() => {
            this.#scene.panCamera(dx * PAN_HOLD_STEP_MM, dy * PAN_HOLD_STEP_MM);
        });
    }

    protected startOrbit(dAzimuth: number, dPolar: number, event: PointerEvent): void {
        this.#capturePointer(event);
        this.#scene.orbitCamera(dAzimuth * ORBIT_STEP_RAD, dPolar * ORBIT_STEP_RAD);
        this.#scheduleHold(() => {
            this.#scene.orbitCamera(dAzimuth * ORBIT_HOLD_STEP_RAD, dPolar * ORBIT_HOLD_STEP_RAD);
        });
    }

    protected startZoom(direction: number, event: PointerEvent): void {
        this.#capturePointer(event);
        this.#scene.zoomCamera(direction * ZOOM_STEP_MM);
        this.#scheduleHold(() => {
            this.#scene.zoomCamera(direction * ZOOM_HOLD_STEP_MM);
        });
    }

    #capturePointer(event: PointerEvent): void {
        try {
            (event.target as HTMLElement).setPointerCapture(event.pointerId);
        } catch {
            // Synthetic event or no active pointer — hold still works via pointerup listener.
        }
    }

    protected stopHold(): void {
        this.#clearHold();
    }

    protected toggleControls(): void {
        this.#clearHold();
        this.controlsVisible.update((visible: boolean) => !visible);
    }

    #scheduleHold(tick: () => void): void {
        this.#clearHold();
        this.#holdTimeout = globalThis.setTimeout(() => {
            this.#holdTimeout = null;
            this.#holdInterval = globalThis.setInterval(tick, HOLD_TICK_MS);
        }, HOLD_DELAY_MS);
    }

    #clearHold(): void {
        if (this.#holdTimeout !== null) {
            globalThis.clearTimeout(this.#holdTimeout);
            this.#holdTimeout = null;
        }
        if (this.#holdInterval !== null) {
            globalThis.clearInterval(this.#holdInterval);
            this.#holdInterval = null;
        }
    }
}
